import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, X as XIcon, Phone, Mail, Send } from 'lucide-react';
import ProjectIcon from '../components/ProjectIcon';
import BlogCard from '../components/BlogCard';
import RichText from '../components/RichText';
import { getLanding, landingPages } from '../data/landing';
import { getProject } from '../data/projects';
import { getPost } from '../data/blog';
import { getService } from '../data/services';
import './Landing.css';

const SITE = 'https://www.pidesk.in';
const CONTACT_EMAIL = 'hq@pidesk.in';
const CONTACT_PHONE = '8887410159';

const EnquiryForm = ({ keyword }) => {
  const [form, setForm] = useState({ name: '', org: '', phone: '', message: '' });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const subject = `Enquiry: ${keyword}`;
    const body = [
      `Name: ${form.name}`,
      `Institute / Company: ${form.org}`,
      `Phone: ${form.phone}`,
      '',
      form.message || `I would like to know more about ${keyword}.`
    ].join('\n');
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <form className="lp-enquiry-form" onSubmit={onSubmit}>
      <div className="lp-enquiry-row">
        <div className="lp-enquiry-field">
          <label htmlFor="lp-name">Your name</label>
          <input id="lp-name" name="name" required value={form.name} onChange={onChange} placeholder="Full name" />
        </div>
        <div className="lp-enquiry-field">
          <label htmlFor="lp-org">Institute / Company</label>
          <input id="lp-org" name="org" required value={form.org} onChange={onChange} placeholder="Where you work" />
        </div>
      </div>
      <div className="lp-enquiry-row">
        <div className="lp-enquiry-field">
          <label htmlFor="lp-phone">Phone number</label>
          <input id="lp-phone" name="phone" required value={form.phone} onChange={onChange} placeholder="For a callback" />
        </div>
        <div className="lp-enquiry-field">
          <label htmlFor="lp-message">What you need (optional)</label>
          <input id="lp-message" name="message" value={form.message} onChange={onChange} placeholder="A line about your requirement" />
        </div>
      </div>
      <button type="submit" className="btn btn-primary btn-lg lp-enquiry-submit">
        Send enquiry <Send size={16} style={{ marginLeft: 8 }} />
      </button>
      <p className="lp-enquiry-note">Opens your email app with these details filled in. Prefer to talk? Call <a href={`tel:+91${CONTACT_PHONE}`}>+91 {CONTACT_PHONE}</a>.</p>
    </form>
  );
};

const LandingPage = ({ slug }) => {
  const page = getLanding(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!page) {
    return (
      <div className="lp-page">
        <div className="container lp-missing">
          <h1>Page not found</h1>
          <Link to="/" className="btn btn-primary">Back home</Link>
        </div>
      </div>
    );
  }

  const url = `${SITE}/${page.slug}`;
  const relatedProduct = page.relatedProduct ? getProject(page.relatedProduct) : null;
  const relatedService = page.relatedService ? getService(page.relatedService) : null;
  const relatedPosts = (page.relatedBlog || []).map((s) => getPost(s)).filter(Boolean);
  const siblings = page.cluster
    ? landingPages.filter((p) => p.cluster === page.cluster && p.slug !== page.slug)
    : [];

  const faqSchema = page.faqs?.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } }))
  } : null;

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: page.keyword,
    name: page.h1,
    description: page.metaDescription,
    provider: { '@type': 'Organization', name: 'Pidesk', url: SITE },
    areaServed: 'IN',
    url
  };

  return (
    <>
      <title>{page.seoTitle}</title>
      <meta name="description" content={page.metaDescription} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={page.seoTitle} />
      <meta property="og:description" content={page.metaDescription} />
      <meta property="og:url" content={url} />
      <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}

      <div className={`lp-page tone-${page.tone}`}>
        {/* ---- Hero: full-bleed mesh band, centred, dual CTA ---- */}
        <section className="lp-hero">
          <div className="lp-hero-mesh" aria-hidden="true" />
          <div className="container lp-hero-inner">
            <span className="lp-kicker">{page.kicker}</span>
            <h1 className="lp-h1">{page.h1}</h1>
            <p className="lp-sub"><RichText text={page.sub} /></p>
            <div className="lp-hero-actions">
              <Link to="/contact" className="btn btn-primary btn-lg">
                Get a free walkthrough <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </Link>
              <Link to="/pricing" className="btn btn-secondary btn-lg">See pricing</Link>
            </div>
            <div className="lp-hero-contact">
              <a href={`tel:+91${CONTACT_PHONE}`}><Phone size={14} /> +91 {CONTACT_PHONE}</a>
              <span className="lp-hero-contact-sep" aria-hidden="true" />
              <a href={`mailto:${CONTACT_EMAIL}`}><Mail size={14} /> {CONTACT_EMAIL}</a>
            </div>
            <div className="lp-stats">
              {page.stats.map((s) => (
                <div key={s.label} className="lp-stat">
                  <strong>{s.value}</strong>
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="container">
          {/* ---- Audience strip ---- */}
          <div className="lp-audience">
            <span className="lp-audience-label">Built for</span>
            <div className="lp-pills">
              {page.audience.map((a) => <span key={a} className="lp-pill">{a}</span>)}
            </div>
          </div>

          {/* ---- About: real, page-specific long-form content ---- */}
          {page.about && (
            <section className="lp-about">
              <h2 className="lp-about-heading">{page.about.heading}</h2>
              <div className="lp-about-body">
                {page.about.paragraphs.map((p, i) => (
                  <p key={i}><RichText text={p} /></p>
                ))}
              </div>
            </section>
          )}

          {/* ---- Modules: each one is its own real page ---- */}
          <section className="lp-section">
            <h2 className="lp-section-title">{page.modulesHeading || 'Everything included'}</h2>
            <p className="lp-modules-hint">Open any module below for the full detail, screens and what it solves.</p>
            <div className="lp-module-grid">
              {page.modules.map((m, i) => (
                <Link key={m.slug} to={`/${page.slug}/${m.slug}`} className="lp-module-card">
                  <div className="lp-module-top">
                    <div className="lp-module-icon"><ProjectIcon name={m.icon} size={26} /></div>
                    <span className="lp-module-num">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h3>{m.title}</h3>
                  <p>{m.short}</p>
                  <span className="lp-module-link">
                    View module <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* ---- Comparison ---- */}
          {page.compare && (
            <section className="lp-section">
              <h2 className="lp-section-title">{page.compare.heading}</h2>
              <div className="lp-compare-wrap">
                <table className="lp-compare-table">
                  <thead>
                    <tr>
                      <th scope="col"></th>
                      {page.compare.cols.map((c, i) => (
                        <th scope="col" key={c} className={i === 0 ? 'lp-col-us' : ''}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {page.compare.rows.map((r) => (
                      <tr key={r.feature}>
                        <th scope="row">{r.feature}</th>
                        {r.values.map((ok, i) => (
                          <td key={i} className={i === 0 ? 'lp-col-us' : ''}>
                            {ok ? <Check size={16} className="lp-yes" /> : <XIcon size={16} className="lp-no" />}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ---- Cross-links to the live product / parent service ---- */}
          {(relatedProduct || relatedService) && (
            <div className="lp-crosslinks">
              {relatedProduct && (
                <Link to={`/products/${relatedProduct.slug}`} className="lp-service-link">
                  <span>See it live in our <strong>{relatedProduct.name}</strong> product</span>
                  <ArrowRight size={16} />
                </Link>
              )}
              {relatedService && (
                <Link to={`/services/${relatedService.slug}`} className="lp-service-link">
                  <span>This is part of our <strong>{relatedService.title}</strong> service</span>
                  <ArrowRight size={16} />
                </Link>
              )}
            </div>
          )}

          {/* ---- Enquiry form: a real, working lead form, not just a CTA link ---- */}
          <section className="lp-section lp-enquiry-section">
            <div className="lp-enquiry-card">
              <div className="lp-enquiry-intro">
                <h2>Get a free walkthrough</h2>
                <p>Tell us a little about your {page.keyword.toLowerCase()} requirement and we will reply with a plan and estimate. No obligation.</p>
                <ul className="lp-enquiry-points">
                  <li><Check size={15} /> Reply within one business day</li>
                  <li><Check size={15} /> A real plan, not a sales pitch</li>
                  <li><Check size={15} /> Call <a href={`tel:+91${CONTACT_PHONE}`}>+91 {CONTACT_PHONE}</a> if you would rather talk now</li>
                </ul>
              </div>
              <EnquiryForm keyword={page.keyword} />
            </div>
          </section>

          {/* ---- Process ---- */}
          <section className="lp-section">
            <h2 className="lp-section-title">How we get you there</h2>
            <ol className="lp-process">
              {page.process.map((step, i) => (
                <li key={step.title} className="lp-process-step">
                  <span className="lp-process-num">{i + 1}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* ---- Mid-page CTA ---- */}
          <section className="lp-cta-band">
            <h2>{page.ctaHeading || `Ready to talk about your ${page.keyword.toLowerCase()}?`}</h2>
            <p>Tell us what you need and we'll reply with a plan and estimate, no obligation.</p>
            <div className="lp-cta-band-actions">
              <Link to="/contact" className="btn btn-primary btn-lg">
                Start a project <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </Link>
              <a href={`tel:+91${CONTACT_PHONE}`} className="btn btn-secondary btn-lg">
                <Phone size={16} style={{ marginRight: 8 }} /> Call +91 {CONTACT_PHONE}
              </a>
            </div>
          </section>

          {/* ---- FAQ ---- */}
          {page.faqs?.length > 0 && (
            <section className="lp-section">
              <h2 className="lp-section-title">Questions about {page.keyword.toLowerCase()}</h2>
              <div className="lp-faq-list">
                {page.faqs.map((f) => (
                  <details key={f.q} className="lp-faq-item">
                    <summary>{f.q}</summary>
                    <p><RichText text={f.a} /></p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* ---- Related reading ---- */}
          {relatedPosts.length > 0 && (
            <section className="lp-section">
              <h2 className="lp-section-title">Related guides</h2>
              <div className="blog-grid">
                {relatedPosts.map((p) => <BlogCard key={p.slug} post={p} />)}
              </div>
            </section>
          )}

          {/* ---- Sibling pages (same cluster) ---- */}
          {siblings.length > 0 && (
            <section className="lp-section lp-siblings-section">
              <h2 className="lp-section-title">Other {page.clusterLabel} pages</h2>
              <div className="lp-sibling-links">
                {siblings.map((s) => (
                  <Link key={s.slug} to={`/${s.slug}`} className="lp-sibling-link">
                    {s.keyword} <ArrowRight size={14} />
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default LandingPage;
